import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { postWithDupCheck } from "@/lib/dupCheck";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, CheckCircle2, ExternalLink, Link2Off, Loader2, Plus, Truck } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(n) || 0);
const today = () => new Date().toISOString().slice(0, 10);

// Dettaglio DDT di una fattura: elenco, apertura, collega esistente, aggiungi nuovo, scollega.
export const FatturaDdtDialog = ({ fid, onClose, onChanged }) => {
  const navigate = useNavigate();
  const [fatt, setFatt] = useState(null);
  const [ddts, setDdts] = useState([]);
  const [liberi, setLiberi] = useState([]);
  const [nuovo, setNuovo] = useState({ numero: "", data: today(), importo: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [{ data: f }, { data: list }] = await Promise.all([api.get(`/fatture/${fid}`), api.get(`/fatture/${fid}/ddt`)]);
    setFatt(f); setDdts(list || []);
    const params = { non_fatturati: true, ...(f.fornitore_id ? { fornitore_id: f.fornitore_id } : {}) };
    const { data: lib } = await api.get("/ddt", { params });
    setLiberi(lib || []);
  };
  useEffect(() => {
    if (fid) { setFatt(null); load().catch(() => toast.error("Errore caricamento DDT")); }
    /* eslint-disable-next-line */
  }, [fid]);

  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); await load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };
  const link = (did) => run(() => api.post(`/fatture/${fid}/link-ddt`, { ddt_id: did }), "DDT collegato");
  const unlink = (did) => run(() => api.post(`/fatture/${fid}/unlink-ddt`, { ddt_id: did }), "DDT scollegato");
  const addNew = () => run(async () => {
    const r = await postWithDupCheck("/ddt", {
      fornitore_id: fatt.fornitore_id || null, cantiere_id: fatt.cantiere_id || null, numero: nuovo.numero,
      data: nuovo.data, importo_stimato: Number(nuovo.importo) || 0, descrizione: `Da fattura n. ${fatt.numero || ""}`,
    });
    if (!r) throw Object.assign(new Error(), { response: { data: { detail: "Aggiunta annullata" } } });
    await api.post(`/fatture/${fid}/link-ddt`, { ddt_id: r.data.id });
    setNuovo({ numero: "", data: today(), importo: "" });
  }, "DDT aggiunto e collegato");
  const openDdt = (did) => { onClose(); navigate(`/fornitori?ddt=${did}`); };

  return (
    <Dialog open={!!fid} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="fattura-ddt-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Truck className="h-4 w-4 text-primary" /> DDT della fattura {fatt ? `n. ${fatt.numero || "s.n."}` : ""}</DialogTitle>
          <DialogDescription>{fatt ? `${fatt.controparte} · ${fatt.data} · ${fmt(fatt.totale)}` : "Caricamento..."}</DialogDescription>
        </DialogHeader>
        {!fatt ? <Loader2 className="h-5 w-5 animate-spin mx-auto my-6" /> : (
          <div className="space-y-4">
            <Quadratura fatt={fatt} ddts={ddts} />
            <ElencoDdt ddts={ddts} busy={busy} openDdt={openDdt} unlink={unlink} />
            <CollegaEsistente liberi={liberi} busy={busy} link={link} />
            <AggiungiNuovo nuovo={nuovo} setNuovo={setNuovo} busy={busy} addNew={addNew} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Quadratura = ({ fatt, ddts }) => {
  if (ddts.length === 0) return null;
  const somma = ddts.reduce((s, d) => s + (Number(d.importo_stimato) || 0), 0);
  const diff = somma - (Number(fatt.imponibile) || 0);
  const ok = Math.abs(diff) <= 1;
  return (
    <div className={`flex items-start gap-2 rounded-md border p-3 text-sm ${ok ? "border-emerald-500/40 bg-emerald-500/10" : "border-red-500/40 bg-red-500/10"}`} data-testid="ddt-quadratura">
      {ok ? <CheckCircle2 className="h-4 w-4 mt-0.5 text-emerald-600" /> : <AlertTriangle className="h-4 w-4 mt-0.5 text-red-600" />}
      <div>
        <div className="font-semibold" data-testid="ddt-quadratura-stato">{ok ? "Quadra" : `Non quadra: differenza ${fmt(diff)}`}</div>
        <div className="text-xs text-muted-foreground">Somma DDT {fmt(somma)} · Imponibile fattura {fmt(fatt.imponibile)}</div>
      </div>
    </div>
  );
};

const ElencoDdt = ({ ddts, busy, openDdt, unlink }) => (
  <div className="border rounded-md divide-y" data-testid="fattura-ddt-list">
    {ddts.length === 0 && <div className="p-4 text-sm text-muted-foreground text-center">Nessun DDT collegato.</div>}
    {ddts.map((d) => (
      <div key={d.id} className="p-2 px-3 flex items-center justify-between gap-2 text-sm" data-testid={`fattura-ddt-row-${d.id}`}>
        <button type="button" className="text-left hover:underline text-primary flex items-center gap-1" onClick={() => openDdt(d.id)} data-testid={`fattura-ddt-open-${d.id}`}>
          <b>n. {d.numero || "s.n."}</b> · {d.data} <ExternalLink className="h-3 w-3" />
        </button>
        <span className="tabular-nums text-xs ml-auto">{d.importo_stimato ? fmt(d.importo_stimato) : "—"}</span>
        <Button size="sm" variant="ghost" disabled={busy} onClick={() => unlink(d.id)} data-testid={`fattura-ddt-unlink-${d.id}`}>
          <Link2Off className="h-3 w-3 mr-1" /> Scollega
        </Button>
      </div>
    ))}
  </div>
);

const CollegaEsistente = ({ liberi, busy, link }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Collega un DDT esistente non fatturato</div>
    <Select value="" onValueChange={link} disabled={busy || liberi.length === 0}>
      <SelectTrigger className="h-9" data-testid="fattura-ddt-link-select">
        <SelectValue placeholder={liberi.length ? `Scegli tra ${liberi.length} DDT` : "Nessun DDT libero"} />
      </SelectTrigger>
      <SelectContent>
        {liberi.map((d) => <SelectItem key={d.id} value={d.id}>n. {d.numero || "s.n."} · {d.data} · {fmt(d.importo_stimato)}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

const AggiungiNuovo = ({ nuovo, setNuovo, busy, addNew }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Aggiungi un nuovo DDT</div>
    <div className="flex flex-wrap gap-2">
      <Input className="h-9 w-32" placeholder="Numero" value={nuovo.numero} onChange={(e) => setNuovo({ ...nuovo, numero: e.target.value })} data-testid="fattura-ddt-new-numero" />
      <Input className="h-9 w-40" type="date" value={nuovo.data} onChange={(e) => setNuovo({ ...nuovo, data: e.target.value })} data-testid="fattura-ddt-new-data" />
      <Input className="h-9 w-32" type="number" step="0.01" placeholder="Importo €" value={nuovo.importo} onChange={(e) => setNuovo({ ...nuovo, importo: e.target.value })} data-testid="fattura-ddt-new-importo" />
      <Button className="h-9" disabled={busy || !nuovo.data} onClick={addNew} data-testid="fattura-ddt-new-save"><Plus className="h-3 w-3 mr-1" /> Aggiungi</Button>
    </div>
  </div>
);
